import React, { useState, useEffect } from 'react';
import { getAllCategories, addCategory, updateCategory, deleteCategory } from '../services/categoryService.js'; // Use the actual service
import '../styles/ManageCategoriesPage.css';

function ManageCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState({ id: null, name: '', description: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Fetch categories on initial load
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setIsLoading(true);
    setError('');
    try {
      const fetchedCategories = await getAllCategories();
      setCategories(fetchedCategories);
    } catch (err) {
      setError('Failed to fetch categories. Please try again.');
      console.error("Fetch Categories Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentCategory({ ...currentCategory, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const categoryData = { name: currentCategory.name, description: currentCategory.description };
    try {
      if (isEditing) {
        await updateCategory(currentCategory.id, categoryData);
      } else {
        await addCategory(categoryData);
      }
      resetForm();
      await fetchCategories(); // Refresh list after successful add/update
    } catch (err) {
      setError(isEditing ? 'Failed to update category. Please try again.' : 'Failed to add category. Please try again.');
      console.error("Submit Category Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (category) => {
    setIsEditing(true);
    setCurrentCategory({ id: category.id, name: category.name, description: category.description });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      setIsLoading(true);
      setError('');
      try {
        await deleteCategory(id);
        await fetchCategories(); // Refresh list after successful delete
      } catch (err) {
        // TODO: Add check here if deletion failed due to linked articles, based on service implementation
        setError('Failed to delete category. It might be linked to articles.');
        console.error("Delete Category Error:", err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const resetForm = () => {
    setIsEditing(false);
    setCurrentCategory({ id: null, name: '', description: '' });
  };

  return (
    <div className="manage-categories-container">
      <h2>Manage Categories</h2>

      {error && <p className="error-message">{error}</p>}

      <form onSubmit={handleSubmit} className="category-form">
        <h3>{isEditing ? 'Edit Category' : 'Add New Category'}</h3>
        <input
          type="text"
          name="name"
          placeholder="Category Name"
          value={currentCategory.name}
          onChange={handleInputChange}
          required
        />
        <textarea
          name="description"
          placeholder="Category Description"
          value={currentCategory.description}
          onChange={handleInputChange}
        />
        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : (isEditing ? 'Update Category' : 'Add Category')}
        </button>
        {isEditing && <button type="button" onClick={resetForm} disabled={isLoading}>Cancel Edit</button>}
      </form>

      <div className="category-list">
        <h3>Existing Categories</h3>
        {isLoading && categories.length === 0 && <p>Loading categories...</p>}
        <ul>
          {categories.map((category) => (
            <li key={category.id}>
              <span><strong>{category.name}</strong>: {category.description}</span>
              <div>
                <button onClick={() => handleEdit(category)} disabled={isLoading}>Edit</button>
                <button onClick={() => handleDelete(category.id)} disabled={isLoading}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default ManageCategoriesPage;
